import { computed, unref, type Ref, type ComputedRef } from 'vue'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query'
import type { AxiosResponse } from 'axios'

import { prepareNewImagesCollection } from '@/utils/propertyDetailsHelpers'
import { selectedAccommodationId } from '@/layouts/queries'
import { useSessionValues } from '@/composables/useSessionValues'
import apis from '@/services'
import type { UIFeatureGroup, PropertyFeature } from '@/types/propertyDetails'
import type { Accommodation } from '@/types'
import type { AccommodationDTORequest, FeatureDto, UpdateImageRequestDto } from '@/services/accommodationService/AccommodationService'


/** Feature groups with UI metadata */
const groups: UIFeatureGroup[] = [
  {
    fieldGroupIds: 1,
    name: 'app_hotel_details_facilities_services',
    slug: 'facilities-and-services',
    icon: 'icon-icn_hotel2_line_dark',
    modifier: 'facilities',
    teaser: 'app_hotel_details_teaser_facilities_and_services',
    hint: 'app_hotel_details_fields_hint',
    fields: null,
  },
  {
    fieldGroupIds: 2,
    name: 'app_hotel_details_health',
    slug: 'health-and-wellness',
    icon: 'icon-icn_spa_line_dark',
    modifier: 'health',
    teaser: 'app_hotel_details_teaser_hotel_details_health',
    fields: null,
  },
  {
    fieldGroupIds: 3,
    name: 'app_hotel_details_food_beverage',
    slug: 'food-and-beverage',
    icon: 'icon-icn_restaurant_line_dark',
    modifier: 'food',
    teaser: 'app_hotel_details_teaser_intro_food',
    fields: null,
  },
  {
    fieldGroupIds: 4,
    name: 'app_hotel_details_family',
    slug: 'family',
    icon: 'icon-icn_family_line_dark',
    modifier: 'family',
    teaser: 'app_hotel_details_teaser_intro_family',
    fields: null,
  },
  {
    fieldGroupIds: 5,
    name: 'app_hotel_details_room_amenities',
    slug: 'room-amenities',
    icon: 'icon-icn_bed_double_line_dark',
    modifier: 'amenities',
    teaser: 'app_hotel_details_teaser_intro_room',
    fields: null,
  },
  {
    fieldGroupIds: 6,
    name: 'app_hotel_details_accessibility',
    slug: 'accessibility',
    icon: 'icon-icn_disabled_dark',
    modifier: 'accessibility',
    teaser: 'app_hotel_details_teaser_intro_accessibility',
    fields: null,
  },
]

interface UpdatePropertyDetailsParams {
  accommodationId: string
  body: AccommodationDTORequest
}

interface SetMainImageParams {
  accommodationId: string
  imageId: string
  organisationId?: string
  imagePath?: string
}

interface DeleteImagesParams {
  accommodationId: string
  body: UpdateImageRequestDto
}

interface SaveFeaturesParams {
  accommodationId: string
  groupId: string
  editObj: FeatureDto[]
}


const fetchAccommodationBasicInfo = async (accommodationId: string): Promise<Accommodation> => {
  const response = await apis.accommodation.fetchNewAccommodationBasicInfo(accommodationId)
  return response.data
}

export function useAccommodationBasicInfo() {
  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: ['accommodationBasicInfo', selectedAccommodationId],
    enabled: computed(() => !!unref(selectedAccommodationId)),
    queryFn: ({ queryKey }) => fetchAccommodationBasicInfo(queryKey[1] as string),
  })

  return {
    isAccommodationBasicInfoLoading: isLoading,
    accommodationBasicInfo: data,
    isAccommodationBasicInfoFetching: isFetching,
    isAccommodationBasicInfoError: isError,
  }
}

export function useHotelChains(languageCode: Ref<string>, namespace: string[]) {
  const { isLoading, data } = useQuery({
    queryKey: ['hotelChains', languageCode.value, namespace],
    queryFn: ({ queryKey }) => apis.accommodation.fetchListTranslations(queryKey[1] as string, queryKey[2] as string[]),
  })

  const hotelChainsData = computed(() => data.value?.data?.[0]?.concepts)
  
  return { hotelChainsLoading: isLoading, hotelChains: hotelChainsData }
}

export function useLodgingTypes(languageCode: Ref<string>, namespace: string[]) {
  const { isLoading, data } = useQuery({
    queryKey: ['lodgingTypes', languageCode, namespace],
    queryFn: ({ queryKey }) => apis.accommodation.fetchListTranslations(queryKey[1] as string, queryKey[2] as string[]),
  })
  
  const lodgingTypesData = computed(() => data.value?.data?.[0]?.concepts)
  
  return { lodgingTypesLoading: isLoading, lodgingTypes: lodgingTypesData }
}

export function useGetImages() {
  const { isLoading, data, isError } = useQuery({
    queryKey: ['images', selectedAccommodationId],
    enabled: computed(() => !!unref(selectedAccommodationId)),
    queryFn: ({ queryKey }) => apis.accommodation.fetchImages(queryKey[1] as string),
  })
  
  const images = computed(() => prepareNewImagesCollection(data.value))

  return { imagesLoading: isLoading, isImagesError: isError, images }
}

export function useGetMainImage() {
  const { isLoading, data, isError } = useQuery({
    queryKey: ['mainImage', selectedAccommodationId],
    enabled: computed(() => !!unref(selectedAccommodationId)),
    queryFn: ({ queryKey }) => apis.accommodation.getMainImage(queryKey[1] as string),
  })

  const mainImage = computed(() => data.value?.data)

  return { isMainImageLoading: isLoading, isMainImageError: isError, mainImage }
}

export function usePropertyFeatures() {
  const { language } = useSessionValues()
  
  const { isLoading, data } = useQuery({
    queryKey: ['propertyFeatures', selectedAccommodationId, language],
    enabled: computed(() => !!unref(selectedAccommodationId)),
    queryFn: async ({ queryKey }) => {
      const accommodationId = queryKey[1] as string
      const lang = queryKey[2] as string
      return await apis.accommodation.fetchAccommodationFields(accommodationId, lang)
    },
  })

  const accommodationFeatures: ComputedRef<UIFeatureGroup[]> = computed(() => { 
    if (!data.value) return groups
    
    return groups.map(group => ({
      ...group,
      fields: data.value?.find(element => element.studioGroupId === group.fieldGroupIds)?.features || null,
    }))
  })

  return { accommodationFeaturesLoading: isLoading, accommodationFeatures }
}

export function useGetFields(fieldGroupId: Ref<string>, locale: Ref<string>) {
  const { isLoading, data } = useQuery({
    queryKey: ['sectionFields', fieldGroupId, locale],
    queryFn: ({ queryKey }) => apis.accommodation.fetchFields(queryKey[1] as string, queryKey[2] as string),
  })

  const sectionFields = computed(() => data.value)

  return { sectionFieldsLoading: isLoading, sectionFields }
}

const getPopularFilters = async (locale: string): Promise<{
  all: PropertyFeature[]
  firstGroup: PropertyFeature[]
  secondGroup: PropertyFeature[]
  fifthGroup: PropertyFeature[]
}> => {
  const results = await Promise.allSettled([
    apis.accommodation.fetchFields('1', locale),
    apis.accommodation.fetchFields('2', locale),
    apis.accommodation.fetchFields('5', locale),
  ])

  const [firstResponse, secondResponse, thirdResponse] = results.map(result => 
    result.status === 'fulfilled' ? result.value : []
  )
  
  const successResults = results
    .filter((result): result is PromiseFulfilledResult<PropertyFeature[]> => result.status === 'fulfilled')
    .flatMap(item => item.value)

  return {
    all: [...successResults],
    firstGroup: firstResponse ?? [],
    secondGroup: secondResponse ?? [],
    fifthGroup: thirdResponse ?? [],
  }
}

export function useGetPopularFilters() {
  const { language } = useSessionValues() 
  
  const { isLoading, data } = useQuery({
    queryKey: ['popularFilters', language],
    queryFn: async ({ queryKey }) => await getPopularFilters(queryKey[1] as string),
  })

  const allPopularFilters = computed(() => data.value?.all)
  const firstGroup = computed(() => data.value?.firstGroup)
  const secondGroup = computed(() => data.value?.secondGroup)
  const fifthGroup = computed(() => data.value?.fifthGroup)

  return { isPopularFiltersLoading: isLoading, allPopularFilters, firstGroup, secondGroup, fifthGroup }
}


export function useSavePropertyBasicInfoUpdate() {
  const queryClient = useQueryClient()
  
  const {
    isPending: savePropertyBasicInfoUpdateLoading,
    isSuccess: savePropertyBasicInfoUpdateSuccess,
    isError: savePropertyBasicInfoUpdateError,
    mutate: savePropertyBasicInfoUpdate,
  } = useMutation({
    mutationFn: ({ accommodationId, body }: UpdatePropertyDetailsParams): Promise<AxiosResponse> => 
      apis.accommodation.updatePropertyDetails({ accommodationId, body }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['accommodationBasicInfo'] })
      queryClient.invalidateQueries({ queryKey: ['images'] })
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      
      const responseData = data?.data
      if (responseData) {
        syncAccommodationBasicInfoUpdateWithOrgAccommodationQuery(responseData, queryClient)
      }
    },
  })

  return {
    savePropertyBasicInfoUpdateLoading,
    savePropertyBasicInfoUpdateSuccess,
    savePropertyBasicInfoUpdateError,
    savePropertyBasicInfoUpdate,
  }
}

export function useSetMainImage() {
  const queryClient = useQueryClient()
  
  const {
    isPending: setMainImageLoading, 
    isSuccess: setMainImageSuccess,
    isError: setMainImageError,
    mutate: setMainImage,
  } = useMutation({
    mutationFn: ({ accommodationId, imageId }: SetMainImageParams): Promise<AxiosResponse> => 
      apis.accommodation.setNewMainImage({ accommodationId, imageId }),
    onSuccess: (_, mutationInput) => {
      if (mutationInput.organisationId && mutationInput.imagePath && mutationInput.accommodationId) {
        queryClient.setQueryData<Accommodation[]>(
          ['organisationAccommodations', mutationInput.organisationId], 
          (oldData) => {
            if (!oldData || !Array.isArray(oldData)) return oldData
            
            return oldData.map(accommodation =>
              Number(accommodation.accommodationId) === Number(mutationInput.accommodationId)
                ? {
                    ...accommodation,
                    mainImageUrls: accommodation.mainImageUrls 
                      ? { ...accommodation.mainImageUrls, imagePath: mutationInput.imagePath! }
                      : null,
                  }
                : accommodation
            )
          }
        )
      }
      queryClient.invalidateQueries({ queryKey: ['images'] })
      queryClient.invalidateQueries({ queryKey: ['accommodationBasicInfo'] })
    },
  })

  return {
    setMainImageLoading,
    setMainImageSuccess,
    setMainImageError,
    setMainImage,
  }
}

export function useDeleteImages() {
  const queryClient = useQueryClient()
  
  const {
    isPending: deleteImagesLoading,
    isSuccess: deleteImagesSuccess,
    isError: deleteImagesError,
    mutate: deleteImages,
  } = useMutation({
    mutationFn: ({ accommodationId, body }: DeleteImagesParams): Promise<AxiosResponse> => 
      apis.accommodation.deleteImage({ accommodationId, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['images'] })
    },
  })

  return {
    deleteImagesLoading,
    deleteImagesSuccess,
    deleteImagesError,
    deleteImages,
  }
}

export function useSaveFeatures() {
  const queryClient = useQueryClient()
  
  const {
    isPending: saveFeaturesLoading,
    isSuccess: saveFeaturesSuccess,
    isError: saveFeaturesError,
    mutate: saveFeatures,
  } = useMutation({
    mutationFn: ({ accommodationId, groupId, editObj }: SaveFeaturesParams): Promise<AxiosResponse> =>
      apis.accommodation.updatePropertyFeatures(accommodationId, groupId, editObj),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propertyFeatures'] })
    },
  })

  return {
    saveFeaturesLoading,
    saveFeaturesSuccess,
    saveFeaturesError,
    saveFeatures,
  }
}


const syncAccommodationBasicInfoUpdateWithOrgAccommodationQuery = (
  newAccommodationBasicInfo: Accommodation, 
  queryClient: QueryClient
): void => {
  const orgAccommodationQueries = queryClient.getQueriesData<Accommodation[]>({ 
    queryKey: ['organisationAccommodations'] 
  })
  
  for (const [orgAccommodationQueryKey, orgAccommodationQueryData] of orgAccommodationQueries) {
    if (orgAccommodationQueryKey?.[0] === 'organisationAccommodations') {
      const orgAccommodationQueryOrgId = orgAccommodationQueryKey?.[1] as string
      
      if (!orgAccommodationQueryOrgId || !orgAccommodationQueryData) continue
      
      const accommodationData = orgAccommodationQueryData.find(accommodation => 
        accommodation.accommodationId === newAccommodationBasicInfo.accommodationId
      )

      if (accommodationData) {
        queryClient.setQueryData<Accommodation[]>(
          ['organisationAccommodations', orgAccommodationQueryOrgId], 
          (oldData) => {
            if (!oldData || !Array.isArray(oldData)) return oldData
            
            return oldData.map(accommodation =>
              accommodation.accommodationId === newAccommodationBasicInfo.accommodationId
                ? { ...accommodation, ...newAccommodationBasicInfo }
                : accommodation
            )
          }
        )
        return
      }
    }
  }
}